import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { HardDrive, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { getToken } from '../../services/tokenService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/nettoyage-pdf`;
const headers = () => ({ Authorization: `Bearer ${getToken()}` });
const fr = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR') : '—');

const Rapport = ({ r }) => (
  <div className="rounded-lg border bg-gray-50 p-3 text-sm space-y-1" data-testid="nettoyage-pdf-rapport">
    <p className="font-semibold">{r.simulation ? 'Dernière simulation' : 'Dernier nettoyage'} — {fr(r.at)}</p>
    <p>{r.nb} fichier(s) {r.simulation ? 'seraient supprimés' : 'supprimés'} — {r.mo_liberes} Mo</p>
    <p className="text-gray-600">{r.nb_sans_copie} gardé(s) faute de copie GED vérifiée · {r.nb_non_eligibles} autre(s) PDF gardé(s)</p>
  </div>
);

export const NettoyagePdf = () => {
  const [open, setOpen] = useState(false);
  const [etat, setEtat] = useState(null);
  const [busy, setBusy] = useState(false);
  const charger = useCallback(async () => setEtat((await axios.get(API, { headers: headers() })).data), []);
  const role = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').role; } catch { return null; } })();
  if (role !== 'super_admin') return null;

  const action = async (url, msg) => {
    setBusy(true);
    try {
      await axios.post(`${API}${url}`, {}, { headers: headers() });
      toast.success(msg);
      setTimeout(charger, 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action impossible');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => { setOpen(true); charger(); }} data-testid="nettoyage-pdf-open-btn">
        <HardDrive className="h-4 w-4 mr-2" />Nettoyage PDF
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="nettoyage-pdf-dialog">
          <DialogHeader><DialogTitle>Nettoyage mensuel des PDF</DialogTitle></DialogHeader>
          {!etat ? <Loader2 className="h-5 w-5 animate-spin" /> : (
            <div className="space-y-3 text-sm">
              <p>Chaque 1er du mois, les reçus et devis PDF de plus de {etat.age_jours} jours dont la copie GED est vérifiée sont retirés du disque. Un rapport vous est envoyé par e-mail.</p>
              <p data-testid="nettoyage-pdf-mode">Mode : <b>{etat.mode === 'actif' ? 'Suppression active' : 'Simulation (rien n\'est supprimé)'}</b></p>
              {etat.dernier_rapport && <Rapport r={etat.dernier_rapport} />}
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" disabled={busy} onClick={() => action('/simulation', 'Simulation lancée, rapport envoyé par e-mail')} data-testid="nettoyage-pdf-simulation-btn">Lancer une simulation</Button>
                {etat.mode === 'actif'
                  ? <Button size="sm" variant="outline" disabled={busy} onClick={() => action('/mode/simulation', 'Suppression désactivée')} data-testid="nettoyage-pdf-desactiver-btn">Revenir en simulation</Button>
                  : <Button size="sm" className="bg-rose-600 hover:bg-rose-700" disabled={busy || !etat.dernier_rapport} onClick={() => action('/mode/actif', 'Suppression activée pour les prochains nettoyages')} data-testid="nettoyage-pdf-activer-btn">Activer la suppression</Button>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
